import { useRef, useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Upload, Download, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { massMigrationApi } from '@/lib/api/massMigration';
import {
  useMassMigrationWizardOrgType,
  useMassMigrationWizardParsedCsv,
  useMassMigrationWizardActions,
} from '@/stores/massMigrationWizardStore';
import { WizardNavigation } from './WizardNavigation';
import { UploadPreviewA } from './upload/UploadPreviewA';
import { UploadPreviewB } from './upload/UploadPreviewB';
import type { MassMigrationOrgType, ParsedCsvResult } from '@/types/massMigration';

// ─── Template generation ──────────────────────────────────────────────────────

const NEW_ORG_COLUMNS = [
  'bridge_id',
  'org_name',
  'street',
  'city',
  'state',
  'zip',
  'owner_first',
  'owner_last',
  'owner_email',
  'owner_phone',
  'billing',
  'send_invite',
  // Required for operators, optional for admins (admins may leave the cells empty).
  'euid',
] as const;

const EXISTING_ORG_COLUMNS = ['bridge_id', 'amplify_org_uuid'] as const;

function getColumns(orgType: MassMigrationOrgType): readonly string[] {
  return orgType === 'New' ? NEW_ORG_COLUMNS : EXISTING_ORG_COLUMNS;
}

function downloadTemplate(orgType: MassMigrationOrgType): void {
  const columns = getColumns(orgType);
  const csv = columns.join(',') + '\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'template.csv';
  // Fix 7: append to DOM before click so Firefox and Safari trigger the download
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── UploadDropzone ───────────────────────────────────────────────────────────

interface UploadDropzoneProps {
  orgType: MassMigrationOrgType;
  isUploading: boolean;
  onFile: (file: File) => void;
}

function UploadDropzone({ orgType, isUploading, onFile }: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const columns = getColumns(orgType);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file !== undefined) {
        onFile(file);
      }
    },
    [onFile],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file !== undefined) {
        onFile(file);
      }
    },
    [onFile],
  );

  return (
    <div className="space-y-6">
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload CSV file"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
        className={cn(
          'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-12 text-center cursor-pointer transition-colors',
          isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          className="sr-only"
          onChange={handleChange}
          aria-hidden="true"
        />
        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">Parsing CSV…</p>
          </div>
        ) : (
          <>
            <Upload className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
            <div>
              <p className="font-medium text-foreground">Drag CSV here or click to browse</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Required columns:{' '}
                <span className="font-mono">
                  {columns
                    .slice(0, orgType === 'New' ? 5 : columns.length)
                    .join(', ')}
                  {orgType === 'New' && ', …'}
                </span>
              </p>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            downloadTemplate(orgType);
          }}
        >
          <Download className="mr-2 h-4 w-4" aria-hidden="true" />
          Download template
        </Button>
      </div>

      <div className="rounded-md bg-muted/50 p-4">
        <p className="mb-2 text-sm font-medium text-foreground">What happens next</p>
        <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
          <li>Your CSV is parsed and columns are auto-matched to expected fields.</li>
          <li>Bridge and Amplify IDs are validated against the source system.</li>
          <li>Detected modules are shown so you can confirm which to migrate per org.</li>
        </ul>
      </div>
    </div>
  );
}

// ─── Preview toggle ───────────────────────────────────────────────────────────

type PreviewMode = 'spreadsheet' | 'compact';

interface PreviewToggleProps {
  mode: PreviewMode;
  onChange: (mode: PreviewMode) => void;
}

function PreviewToggle({ mode, onChange }: PreviewToggleProps) {
  return (
    <div className="inline-flex rounded-md border border-border bg-muted p-0.5">
      {(['spreadsheet', 'compact'] as PreviewMode[]).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={cn(
            'rounded px-3 py-1 text-xs font-medium capitalize transition-colors',
            mode === m
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
          aria-pressed={mode === m}
        >
          {m === 'spreadsheet' ? 'Spreadsheet' : 'Compact'}
        </button>
      ))}
    </div>
  );
}

// ─── UploadStep ───────────────────────────────────────────────────────────────

export function UploadStep() {
  const orgType = useMassMigrationWizardOrgType();
  const parsedCsv = useMassMigrationWizardParsedCsv();
  const { setParsedCsv, setStep } = useMassMigrationWizardActions();

  const [fileName, setFileName] = useState<string>('');
  const [previewMode, setPreviewMode] = useState<PreviewMode>('spreadsheet');

  const uploadMutation = useMutation({
    mutationFn: ({ file, type }: { file: File; type: MassMigrationOrgType }) =>
      massMigrationApi.validateCsv(file, type),
    onSuccess: (result: ParsedCsvResult) => {
      setParsedCsv(result);
    },
  });

  const handleFile = useCallback(
    (file: File) => {
      if (orgType === null) return;
      setFileName(file.name);
      uploadMutation.mutate({ file, type: orgType });
    },
    [orgType, uploadMutation],
  );

  const handleReplace = useCallback(() => {
    setFileName('');
    uploadMutation.reset();
    setParsedCsv({ totalRows: 0, rows: [], errors: [] });
  }, [uploadMutation, setParsedCsv]);

  const canProceed =
    parsedCsv !== null && parsedCsv.rows.length > 0 && parsedCsv.errors.length === 0;

  const isUploaded =
    parsedCsv !== null &&
    (parsedCsv.rows.length > 0 || parsedCsv.errors.length > 0) &&
    !uploadMutation.isPending;

  return (
    <div className="space-y-6">
      {isUploaded ? (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Preview your upload below. Fix any errors before continuing.
            </p>
            <PreviewToggle mode={previewMode} onChange={setPreviewMode} />
          </div>

          {/* Fix 3: pass orgType to UploadPreviewA so it shows correct columns */}
          {previewMode === 'spreadsheet' ? (
            <UploadPreviewA
              result={parsedCsv}
              fileName={fileName}
              orgType={orgType ?? 'New'}
              onReplace={handleReplace}
            />
          ) : (
            <UploadPreviewB
              result={parsedCsv}
              fileName={fileName}
              onReplace={handleReplace}
            />
          )}
        </>
      ) : (
        <UploadDropzone
          orgType={orgType ?? 'New'}
          isUploading={uploadMutation.isPending}
          onFile={handleFile}
        />
      )}

      {uploadMutation.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Upload failed</AlertTitle>
          <AlertDescription>
            {uploadMutation.error instanceof Error
              ? uploadMutation.error.message
              : 'An unexpected error occurred. Please try again.'}
          </AlertDescription>
        </Alert>
      )}

      <WizardNavigation
        onBack={() => setStep(0)}
        onNext={canProceed ? () => setStep(2) : undefined}
        nextDisabled={!canProceed}
        nextLabel="Validate IDs"
      />
    </div>
  );
}
